import { m } from "@plannello/i18n/messages";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Form } from "react-aria-components";
import { AuthCard } from "@/components/shell/AuthCard";
import { LocaleSwitch } from "@/components/shell/LocaleSwitch";
import { Button, buttonStyles, InlineError, PasswordField, Select, Skeleton, TextField } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { focusFirstInvalid } from "@/lib/forms";
import { applyLocale, LOCALES } from "@/lib/locale";
import { type Locale, meQuery } from "@/lib/queries";
import { describeError } from "@/lib/session";

export const Route = createFileRoute("/invite/$token")({ component: InvitePage });

type Invite = {
  email: string;
  name: string | null;
  locale: Locale;
  expiresAt: string;
  inviterName: string | null;
  instanceName: string;
};

/** Why a link cannot be used, in words that say what to do next. */
function linkProblem(e: unknown): { title: string; body: string } | null {
  if (!(e instanceof ApiError)) return null;
  switch (e.code) {
    case "invite_used":
      return { title: m.invite_used_title(), body: m.invite_used_body() };
    case "invite_revoked":
      return { title: m.invite_revoked_title(), body: m.invite_revoked_body() };
    case "invite_expired":
      return { title: m.invite_expired_title(), body: m.invite_expired_body() };
    case "invite_invalid":
      return { title: m.invite_invalid_title(), body: m.invite_invalid_body() };
    default:
      return null;
  }
}

function InvitePage() {
  const { token } = Route.useParams();
  const q = useQuery({
    queryKey: ["invite", token],
    queryFn: () => api.get<Invite>(`/api/v1/auth/invites/${token}`),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });

  // The invitation says which language to start in.
  const invitedLocale = q.data?.locale;
  useEffect(() => {
    if (invitedLocale) applyLocale(invitedLocale);
  }, [invitedLocale]);

  if (q.isPending) {
    return (
      <AuthCard title={m.invite_title_loading()}>
        <div role="status" aria-busy="true" className="flex flex-col gap-4">
          <span className="sr-only">{m.common_loading()}</span>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </AuthCard>
    );
  }

  if (q.isError) {
    const problem = linkProblem(q.error);
    if (problem) {
      return (
        <AuthCard title={problem.title} intro={problem.body} footer={<LocaleSwitch />}>
          <Link
            to="/login"
            search={{ redirect: undefined, error: undefined }}
            className={buttonStyles({ variant: "secondary", size: "lg", className: "w-full" })}
          >
            {m.auth_sign_in()}
          </Link>
        </AuthCard>
      );
    }
    return (
      <AuthCard title={m.invite_title_loading()}>
        <InlineError
          message={
            q.error instanceof ApiError && q.error.status === 429
              ? m.error_rate_limited()
              : describeError(q.error, { network: m.error_network(), generic: m.error_generic() })
          }
          onRetry={() => void q.refetch()}
          isRetrying={q.isFetching}
        />
      </AuthCard>
    );
  }

  return <AcceptForm token={token} invite={q.data} />;
}

function AcceptForm({ token, invite }: { token: string; invite: Invite }) {
  const router = useRouter();
  const qc = useQueryClient();
  const [name, setName] = useState(invite.name ?? "");
  const [password, setPassword] = useState("");
  const [locale, setLocale] = useState<Locale>(invite.locale);
  const [errors, setErrors] = useState<{ name?: string; password?: string; form?: string }>({});
  const [pending, setPending] = useState(false);
  const [closed, setClosed] = useState<{ title: string; body: string } | null>(null);

  async function submit() {
    const next: typeof errors = {};
    if (!name.trim()) next.name = m.validation_required();
    if (password.length < 10) next.password = m.sec_err_too_short();
    setErrors(next);
    if (next.name || next.password) {
      focusFirstInvalid();
      return;
    }
    setPending(true);
    try {
      const res = await api.post<{ userId: string; signedIn: boolean }>(`/api/v1/auth/invites/${token}/accept`, {
        name: name.trim(),
        password,
        locale,
      });
      qc.removeQueries({ queryKey: meQuery.queryKey });
      if (res.signedIn) await router.navigate({ to: "/" });
      else await router.navigate({ to: "/login", search: { redirect: undefined, error: undefined } });
    } catch (e) {
      const problem = linkProblem(e);
      if (problem) setClosed(problem);
      else if (e instanceof ApiError && e.code === "email_taken") setErrors({ form: m.invite_email_taken() });
      else if (e instanceof ApiError && e.fields?.password) setErrors({ password: m.sec_err_too_short() });
      else setErrors({ form: describeError(e, { network: m.error_network(), generic: m.error_generic() }) });
    } finally {
      setPending(false);
    }
  }

  if (closed) {
    return (
      <AuthCard title={closed.title} intro={closed.body} footer={<LocaleSwitch />}>
        <Link
          to="/login"
          search={{ redirect: undefined, error: undefined }}
          className={buttonStyles({ variant: "secondary", size: "lg", className: "w-full" })}
        >
          {m.auth_sign_in()}
        </Link>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={m.invite_title({ instance: invite.instanceName })}
      intro={invite.inviterName ? m.invite_intro_by({ inviter: invite.inviterName }) : m.invite_intro()}
      footer={<LocaleSwitch />}
    >
      <Form
        validationBehavior="aria"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {errors.form ? <InlineError message={errors.form} /> : null}
        <TextField
          label={m.auth_email()}
          name="email"
          type="email"
          value={invite.email}
          isReadOnly
          autoComplete="username"
        />
        <TextField
          label={m.auth_name()}
          name="name"
          autoComplete="name"
          autoFocus
          value={name}
          onChange={setName}
          error={errors.name}
        />
        <PasswordField
          label={m.auth_password()}
          description={m.sec_password_rule()}
          name="password"
          autoComplete="new-password"
          value={password}
          onChange={(v) => {
            setPassword(v);
            setErrors({ ...errors, password: undefined });
          }}
          error={errors.password}
        />
        <Select
          id="invite-language"
          label={m.settings_language()}
          items={LOCALES.map((l) => ({ id: l.value, label: l.label }))}
          selectedKey={locale}
          onSelectionChange={(k) => {
            setLocale(k as Locale);
            applyLocale(k, { refocusId: "invite-language" });
          }}
        />
        <Button type="submit" variant="primary" size="lg" isPending={pending} className="mt-2 w-full">
          {m.invite_accept()}
        </Button>
      </Form>
    </AuthCard>
  );
}
